import React, { useCallback, useEffect, useState } from "react"
import { gql, useMutation, useQuery } from "@apollo/client"

import { Formik, Form } from "formik"

import Layout from "components/Layout"
import Avatar from "react-avatar"
import { Fieldset, Modal } from "components"

import useInfiniteScroll from "hooks/useInfiniteScroll"
import { toast } from "react-hot-toast"

import { uri } from "config"

const AGENCIES = gql`
  query Agencies(
    $filter: AgencyFilterInput
    $page: Int
    $size: Int
    $sort: AgencySortInput
  ) {
    agencies(filter: $filter, page: $page, size: $size, sort: $sort) {
      id
      name
      description
      logoURL
    }
  }
`
const CREATE_AGENCY = gql`
  mutation CreateAgency($input: CreateAgencyInput!) {
    createAgency(input: $input) {
      id
      name
      description
      logoURL
    }
  }
`
const UPDATE_AGENCY = gql`
  mutation UpdateAgency($input: UpdateAgencyInput!) {
    updateAgency(input: $input) {
      id
      name
      description
      logoURL
    }
  }
`
const DELETE_AGENCY = gql`
  mutation DeleteAgency($id: ID!) {
    deleteAgency(id: $id) {
      id
    }
  }
`

const size = 20
const AgenciesPage = () => {
  const [createAgency] = useMutation(CREATE_AGENCY)
  const [updateAgency] = useMutation(UPDATE_AGENCY)
  const [deleteAgency] = useMutation(DELETE_AGENCY)

  const handleCreate = async (input) => {
    const loadingId = toast.loading("Saving...")
    try {
      const user = await createAgency({ variables: { input } })
      toast.dismiss(loadingId)
      toast.success("Agency created!")
      return user
    } catch (error) {
      toast.dismiss(loadingId)
      toast.error("Could not save.")
      throw error
    }
  }

  const handleUpdate = async (input) => {
    const loadingId = toast.loading("Saving...")
    try {
      const user = await updateAgency({ variables: { input } })
      toast.dismiss(loadingId)
      toast.success("Agency updated!")
      return user
    } catch (error) {
      toast.dismiss(loadingId)
      toast.error("Could not save.")
      throw error
    }
  }

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this agency?")) {
      const loadingId = toast.loading("Saving...")
      try {
        await deleteAgency({ variables: { id } })
        toast.dismiss(loadingId)
        toast.success("Agency deleted!")
      } catch (error) {
        toast.dismiss(loadingId)
        toast.error("Could not save.")
        throw error
      }
    }
  }

  const { data, loading, error, fetchMore } = useQuery(AGENCIES, {
    variables: {
      page: 0,
      size: 20,
      //   sort: { firstName: "ASC" },
    },
  })

  const [selectedAgency, setSelectedAgency] = useState(null)
  const [isEditing, setIsEditing] = useState(false)

  const openModal = useCallback(async () => {
    setIsEditing(true)
  }, [setIsEditing])

  const closeModal = useCallback(async () => {
    setIsEditing(false)
    setSelectedAgency(null)
  }, [setIsEditing])

  const handleSearch = useCallback(
    (search) => {
      if (!fetchMore) return

      //   if (search?.trim() === "") {
      //     fetchMore({
      //       updateQuery: (
      //         previousResult,
      //         { fetchMoreResult, queryVariables },
      //       ) => {
      //         return {
      //           passengers: [...fetchMoreResult.passengers],
      //         }
      //       },
      //     })

      //     return
      //   }

      //   fetchMore({
      //     variables: { filter: { search } },
      //     updateQuery: (previousResult, { fetchMoreResult, queryVariables }) => {
      //       return {
      //         passengers: [
      //           // ...previousResult.matches,
      //           ...fetchMoreResult.passengers,
      //         ],
      //       }
      //     },
      //   })
    },
    [fetchMore],
  )

  const handleLoadMore = useCallback(
    (page) => {
      if (page === 0) return

      //   fetchMore({
      //     variables: { page },
      //     updateQuery: (previousResult, { fetchMoreResult }) => {
      //       if (!fetchMoreResult) return previousResult
      //       return Object.assign({}, previousResult, {
      //         passengers: [
      //           ...previousResult.passengers,
      //           ...fetchMoreResult.passengers,
      //         ],
      //       })
      //     },
      //   })
    },
    [fetchMore],
  )

  const exportAgencies = async () => {
    if (window.confirm(`Are you sure you want to export all agencies?`)) {
      try {
        await toast.promise(
          fetch(`${uri}/reporting/passenger/xlsx/`)
            .then((_) => _.blob())
            .then((blob) => {
              const file = window.URL.createObjectURL(blob)
              window.location.assign(file)
            }),
          {
            loading: "Creating export...",
            success: <b>Export created!</b>,
            error: <b>Could not save.</b>,
          },
        )
      } catch (error) {}
    }
  }

  const handleSelect = (agency) => {
    setSelectedAgency(agency)
    openModal()
  }

  return (
    <Layout title="Agencies | GTR" isLoading={loading}>
      <BoxHeader
        title="Agencies"
        onExport={exportAgencies}
        onSearch={handleSearch}
        onClick={openModal}
      />

      <AgenciesTable
        loading={loading}
        data={data?.agencies}
        onLoadMore={handleLoadMore}
        onSelect={handleSelect}
      />

      <AgencyModalForm
        data={selectedAgency}
        isOpen={isEditing}
        onClose={closeModal}
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
      />
    </Layout>
  )
}

export default AgenciesPage

const BoxHeader = ({ title, onSearch, onExport, onClick }) => {
  const [query, setQuery] = useState("")

  useEffect(() => {
    onSearch && onSearch(query)
  }, [query])

  return (
    <header className="bg-gray-800 text-gray-100 pb-24">
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-12 gap-4">
        <div className="col-span-3 flex items-center">
          <h1 className="font-black text-3xl">{title}</h1>
        </div>

        <div className="col-span-6 relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-4">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 fill-current text-gray-600"
            >
              <path d="M16.32 14.9l1.1 1.1c.4-.02.83.13 1.14.44l3 3a1.5 1.5 0 0 1-2.12 2.12l-3-3a1.5 1.5 0 0 1-.44-1.14l-1.1-1.1a8 8 0 1 1 1.41-1.41l.01-.01zM10 16a6 6 0 1 0 0-12 6 6 0 0 0 0 12z" />
            </svg>
          </div>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search"
            className="block w-full border border-transparent bg-gray-900 focus:outline-none focus:bg-white focus:border-gray-300 text-gray-900 rounded-lg pl-12 pr-4 py-2"
          />
        </div>

        <div className="col-span-3 flex space-x-4 justify-end items-center pl-8">
          <button
            onClick={onExport}
            type="button"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
          </button>

          <button
            onClick={onClick}
            type="button"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="-ml-1 mr-2 h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            Agency
          </button>
        </div>
      </div>
    </header>
  )
}

const AgenciesTable = ({ loading, data, onSelect, onLoadMore }) => {
  const header = ["Name", ""]

  const hasMore = loading ? false : data?.length >= size
  const [page, loaderRef] = useInfiniteScroll({ hasMore })

  useEffect(() => {
    onLoadMore && onLoadMore(page)
  }, [page])

  return (
    <main className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 pb-32">
      <div className="relative -mt-16 z-30">
        <div className="bg-white rounded-lg shadow-lg">
          <div className="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200">
            <div style={{ maxHeight: "70vh", overflowY: "scroll" }}>
              <table className="min-w-full table-auto">
                <thead>
                  <tr>
                    {header.map((item, index) => (
                      <th
                        key={index}
                        className="px-6 py-3 border-b border-gray-200 bg-gray-100 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                        style={{ position: "sticky", top: "0" }}
                      >
                        {item}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="table-body">
                  {data?.map((agency, index) => (
                    <tr
                      key={index}
                      className="table-row"
                      onClick={() => onSelect(agency)}
                    >
                      <td className="table-cell w-6/12">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <Avatar
                              name={agency?.name}
                              src={agency?.logoURL}
                              size="40"
                              round={true}
                            />
                          </div>
                          <div className="ml-4">
                            <div className="text-sm leading-5 font-medium text-gray-900">
                              {agency?.name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell w-6/12">
                        <div className="text-sm leading-5 text-gray-900">
                          {agency?.description}
                        </div>
                      </td>
                      {/* <td className="table-cell w-3/12">
                        <div className="text-sm leading-5 text-gray-900">
                          {formatPhoneNumber(passenger?.phone)}
                        </div>
                      </td>
                      <td className="table-cell w-3/12">
                        <div className="text-sm leading-5 text-gray-900">
                          {passenger?.passportNumber}
                        </div>
                      </td> */}
                    </tr>
                  ))}
                </tbody>
              </table>

              {hasMore && (
                <div ref={loaderRef} className="text-center bg-gray-100">
                  Loading…
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </main>
  )
}

const AgencyModalForm = ({
  data,
  isOpen,
  onClose,
  onCreate,
  onUpdate,
  onDelete,
}) => {
  const agencyMock = {
    name: "Alpine Adventures",
    description: "Global ski & adventure specialist",
    logoURL: "",
  }

  const agencyModel = {
    name: "",
    description: "",
    logoURL: "",
  }

  const formikProps = {
    initialValues: { ...agencyModel, ...(data ? data : {}) },
    validateOnBlur: false,
    validateOnChange: false,
    // validationSchema: createTripSchema,
    onSubmit: async (values, { setFieldError }) => {
      try {
        const input = {
          ...values,
          logoURL: values?.logoURL?.url
            ? values?.logoURL?.url
            : values?.logoURL,
        }
        values.id ? onUpdate(input) : onCreate(input)
        onClose()
      } catch (e) {
        const errors = e.graphQLErrors.map((_) => _)
        setFieldError("server", errors)
      }
    },
  }

  return (
    <Modal title="Create Agency" isOpen={isOpen} onClose={onClose}>
      <div className="max-w-screen-lg mx-auto ">
        <Formik {...formikProps}>
          {({ errors, setFieldError, isSubmitting }) => {
            const handleDelete = async () => {
              try {
                await onDelete(data.id)
                await onClose()
              } catch (e) {
                const errors = e.graphQLErrors.map((_) => _)
                setFieldError("server", errors)
              }
            }

            return (
              <Form>
                <div className="flex">
                  <div className="px-2 py-3 w-3/12">
                    <Fieldset
                      component="image"
                      multiple={false}
                      library={false}
                      variant="round"
                      name={`logoURL`}
                      label="Logo"
                      preload={true}
                    />
                  </div>

                  <div className="px-2 w-6/12">
                    <div className="px-2 py-3">
                      <Fieldset name={`name`} label="Name " type="text" />
                    </div>

                    <div className="px-2 py-3">
                      <Fieldset
                        name={`description`}
                        label="Description "
                        component="textarea"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  {data?.id && (
                    <button
                      type="button"
                      onClick={handleDelete}
                      className="h-10 px-5 m-2 text-red-100 transition-colors duration-150 bg-red-700 rounded-lg focus:shadow-outline hover:bg-red-800"
                    >
                      Delete Agency
                    </button>
                  )}
                </div>

                <div className="py-3">
                  {errors?.server?.map(({ code, message }) => (
                    <div role="alert">
                      <div className="bg-red-500 text-white font-bold rounded-t px-4 py-2">
                        {code}
                      </div>
                      <div className="border border-t-0 border-red-400 rounded-b bg-red-100 px-4 py-3 text-red-700">
                        <p>{message}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <ModalNavigation {...{ onClose, isSubmitting }} />
              </Form>
            )
          }}
        </Formik>
      </div>
    </Modal>
  )
}

const ModalNavigation = ({ onClose, isSubmitting }) => {
  return (
    <div className=" px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
      <span className="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
        <button
          disabled={isSubmitting}
          type="submit"
          className="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-green-500 focus:outline-none focus:border-green-700 focus:shadow-outline-green transition ease-in-out duration-150 sm:text-sm sm:leading-5 disabled:opacity-50 disabled:cursor-wait"
        >
          Save
        </button>
      </span>

      <span className="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
        <button
          onClick={onClose}
          type="button"
          className="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue transition ease-in-out duration-150 sm:text-sm sm:leading-5"
        >
          Cancel
        </button>
      </span>
    </div>
  )
}
